import { useState, useMemo } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  EnvelopeIcon,
  PencilIcon,
  PlusIcon,
  LinkIcon,
  CircleIcon,
  EyeIcon,
  TrashIcon,
  CurrencyDollarIcon,
  PercentIcon,
  CheckCircleIcon,
  PaperPlaneRightIcon,
  CalendarIcon,
  MapPinIcon,
} from '@phosphor-icons/react';
import { AgentIcon } from '@/components/common/AgentIcon';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { getEntityDisplayName } from '@/components/system-entities/utils/display-names';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useActivityLog } from '@/components/activity-log/useActivityLog';
import { useActivityGraphContext } from '../ActivityGraphContext';
import type { SystemEntity } from '@erp/shared-types';
import type { ActivityItem, ActivityItemType } from '@erp/rpc';

interface ActivityLogTabProps {
  entityType: SystemEntity;
  entityId: string;
}

type GroupedActivityItem =
  | ActivityItem
  | { type: 'email_group'; emails: ActivityItem[]; id: string };

function isEmailGroup(
  item: GroupedActivityItem,
): item is { type: 'email_group'; emails: ActivityItem[]; id: string } {
  return 'emails' in item && item.type === 'email_group';
}

function AiActionIcon({ className }: { className?: string }) {
  const sizeMatch = className?.match(/h-(\d+(?:\.\d+)?)/);
  const size = sizeMatch ? parseFloat(sizeMatch[1]) * 6 : 18;
  return <AgentIcon state="agent" size={size} />;
}

function getAiActionConfig(actionType: string | undefined) {
  switch (actionType) {
    case 'remove_line_item':
      return {
        icon: TrashIcon,
        bgColor: 'bg-red-100 dark:bg-red-900',
        iconColor: 'text-red-600 dark:text-red-300',
        ringColor: 'ring-red-400',
        label: 'Removed item',
      };
    case 'update_line_item_price':
      return {
        icon: CurrencyDollarIcon,
        bgColor: 'bg-emerald-100 dark:bg-emerald-900',
        iconColor: 'text-emerald-600 dark:text-emerald-300',
        ringColor: 'ring-emerald-400',
        label: 'Updated price',
      };
    case 'create_discount':
      return {
        icon: PercentIcon,
        bgColor: 'bg-amber-100 dark:bg-amber-900',
        iconColor: 'text-amber-600 dark:text-amber-300',
        ringColor: 'ring-amber-400',
        label: 'Created discount',
      };
    case 'approve_order':
      return {
        icon: CheckCircleIcon,
        bgColor: 'bg-green-100 dark:bg-green-900',
        iconColor: 'text-green-600 dark:text-green-300',
        ringColor: 'ring-green-400',
        label: 'Verified order',
      };
    case 'send_email_draft':
      return {
        icon: PaperPlaneRightIcon,
        bgColor: 'bg-blue-100 dark:bg-blue-900',
        iconColor: 'text-blue-600 dark:text-blue-300',
        ringColor: 'ring-blue-400',
        label: 'Sent email',
      };
    case 'update_order_dates':
      return {
        icon: CalendarIcon,
        bgColor: 'bg-violet-100 dark:bg-violet-900',
        iconColor: 'text-violet-600 dark:text-violet-300',
        ringColor: 'ring-violet-400',
        label: 'Updated dates',
      };
    case 'update_ship_to_location':
      return {
        icon: MapPinIcon,
        bgColor: 'bg-cyan-100 dark:bg-cyan-900',
        iconColor: 'text-cyan-600 dark:text-cyan-300',
        ringColor: 'ring-cyan-400',
        label: 'Updated location',
      };
    default:
      return {
        icon: AiActionIcon,
        bgColor: 'bg-stone-100 dark:bg-stone-800',
        iconColor: '',
        ringColor: 'ring-stone-400',
        label: 'AI Action',
      };
  }
}

function getActivityTypeConfig(
  type: ActivityItemType,
  metadata?: Record<string, unknown>,
) {
  switch (type) {
    case 'created':
      return {
        icon: PlusIcon,
        bgColor: 'bg-green-100 dark:bg-green-600',
        iconColor: 'text-green-600 dark:text-white',
        ringColor: 'ring-green-400',
        label: 'Created',
      };
    case 'update':
      return {
        icon: PencilIcon,
        bgColor: 'bg-blue-100 dark:bg-blue-600',
        iconColor: 'text-blue-600 dark:text-white',
        ringColor: 'ring-blue-400',
        label: 'Updated',
      };
    case 'status_change':
      return {
        icon: CircleIcon,
        bgColor: 'bg-purple-100 dark:bg-purple-600',
        iconColor: 'text-purple-600 dark:text-white',
        ringColor: 'ring-purple-400',
        label: 'Status changed',
      };
    case 'email':
      return {
        icon: EnvelopeIcon,
        bgColor: 'bg-gray-100 dark:bg-zinc-700',
        iconColor: 'text-gray-600 dark:text-white',
        ringColor: 'ring-gray-400',
        label: 'Email',
      };
    case 'linked':
      return {
        icon: LinkIcon,
        bgColor: 'bg-gray-100 dark:bg-zinc-700',
        iconColor: 'text-gray-600 dark:text-white',
        ringColor: 'ring-gray-400',
        label: 'Linked',
      };
    case 'ai_action': {
      const actionType = metadata?.actionType as string | undefined;
      return getAiActionConfig(actionType);
    }
    default:
      return {
        icon: CircleIcon,
        bgColor: 'bg-gray-100 dark:bg-zinc-700',
        iconColor: 'text-gray-600 dark:text-white',
        ringColor: 'ring-gray-400',
        label: 'Activity',
      };
  }
}

function getUpdateDescriptionFromPath(
  path: string | undefined,
  changes: Record<string, unknown> | undefined,
): string | null {
  if (!path) return null;

  if (path.includes('/customer-products/match')) {
    return 'matched line item to product';
  }
  if (path.includes('/line-item')) {
    const after = changes?.after as Record<string, unknown> | undefined;
    const before = changes?.before as Record<string, unknown> | undefined;
    if (after && before) {
      const changedFields: string[] = [];
      if (after.quantity !== before.quantity) changedFields.push('quantity');
      if (after.unitPrice !== before.unitPrice) changedFields.push('price');
      if (after.customerDescription !== before.customerDescription)
        changedFields.push('description');
      if (after.customerIdentifier !== before.customerIdentifier)
        changedFields.push('identifier');
      if (changedFields.length > 0) {
        return `updated line item ${changedFields.join(', ')}`;
      }
    }
    return 'updated line item';
  }
  if (path.includes('/customer')) {
    return 'changed customer';
  }
  if (path.includes('/field')) {
    const field = changes?.field as string | undefined;
    if (field) {
      const fieldLabels: Record<string, string> = {
        shipDate: 'ship date',
        deliveryDate: 'delivery date',
        purchaseOrderNumber: 'PO number',
        shipToLocationId: 'ship-to location',
        billToLocationId: 'bill-to location',
      };
      return `updated ${fieldLabels[field] || field}`;
    }
  }

  return null;
}

function getAiActionLabel(actionType: string | undefined): string {
  switch (actionType) {
    case 'remove_line_item':
      return 'removed line item';
    case 'update_line_item_price':
      return 'updated price';
    case 'create_discount':
      return 'created discount';
    case 'approve_order':
      return 'verified order';
    case 'send_email_draft':
      return 'email draft';
    case 'update_order_dates':
      return 'updated dates';
    case 'update_ship_to_location':
      return 'updated ship-to';
    default:
      return 'action';
  }
}

function formatActivityDescription(
  item: ActivityItem,
  entityType: SystemEntity,
): string {
  const { type, metadata, actor } = item;
  const actorName = actor?.name || 'System';

  switch (type) {
    case 'created': {
      if (!actor) {
        return `System created this ${getEntityDisplayName(entityType)}`;
      }
      return `${actorName} created this`;
    }
    case 'update': {
      const path = metadata?.path as string | undefined;
      const changes = metadata?.changes as Record<string, unknown> | undefined;
      const description = getUpdateDescriptionFromPath(path, changes);
      if (description) {
        return `${actorName} ${description}`;
      }
      const field = metadata?.field as string | undefined;
      return `${actorName} updated ${field || 'details'}`;
    }
    case 'status_change': {
      const path = metadata?.path as string | undefined;
      if (path?.includes('/approve')) {
        return `${actorName} approved`;
      }
      if (path?.includes('/reject')) {
        return `${actorName} rejected`;
      }
      const from = metadata?.from as string | undefined;
      const to = metadata?.to as string | undefined;
      if (from && to) {
        return `${actorName} moved from ${from} to ${to}`;
      }
      return `${actorName} changed status`;
    }
    case 'email': {
      const subject = metadata?.subject as string | undefined;
      return subject || 'No subject';
    }
    case 'linked': {
      const linkedType = metadata?.linkedEntityType as string | undefined;
      return `${actorName} linked ${linkedType || 'entity'}`;
    }
    case 'ai_action': {
      const actionType = metadata?.actionType as string | undefined;
      const actionLabel = getAiActionLabel(actionType);
      // Use the actor name (user who approved the action) as the subject
      return `${actorName} ${actionLabel}`;
    }
    default:
      return `${actorName} performed action`;
  }
}

function getActorInitials(name?: string): string {
  if (!name) return 'S';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatFullDateTime(timestamp: string | Date): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return format(date, 'yyyy-MM-dd h:mm a');
}

function groupConsecutiveEmails(items: ActivityItem[]): GroupedActivityItem[] {
  const result: GroupedActivityItem[] = [];
  let currentEmailGroup: ActivityItem[] = [];

  for (const item of items) {
    if (item.type === 'email') {
      currentEmailGroup.push(item);
    } else {
      if (currentEmailGroup.length > 1) {
        result.push({
          type: 'email_group',
          emails: currentEmailGroup,
          id: `email-group-${currentEmailGroup[0].id}`,
        });
      } else if (currentEmailGroup.length === 1) {
        result.push(currentEmailGroup[0]);
      }
      currentEmailGroup = [];
      result.push(item);
    }
  }

  if (currentEmailGroup.length > 1) {
    result.push({
      type: 'email_group',
      emails: currentEmailGroup,
      id: `email-group-${currentEmailGroup[0].id}`,
    });
  } else if (currentEmailGroup.length === 1) {
    result.push(currentEmailGroup[0]);
  }

  return result;
}

function ActivityItemPopoverContent({
  item,
  entityType,
  onViewEmail,
}: {
  item: ActivityItem;
  entityType: SystemEntity;
  onViewEmail?: (emailId: string) => void;
}) {
  const config = getActivityTypeConfig(item.type, item.metadata);
  const Icon = config.icon;
  const description = formatActivityDescription(item, entityType);
  const timeAgo = formatDistanceToNow(new Date(item.timestamp), {
    addSuffix: false,
  });
  const fullDateTime = formatFullDateTime(item.timestamp);

  const emailId =
    item.type === 'email' ? (item.metadata?.emailId as string) : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
            config.bgColor,
          )}
        >
          <Icon className={cn('h-3 w-3', config.iconColor)} />
        </div>
        <span className="text-xs font-medium text-gray-900 dark:text-zinc-100">
          {config.label}
        </span>
        <span
          className="text-xs text-gray-500 dark:text-zinc-500"
          title={fullDateTime}
        >
          {timeAgo} ago
        </span>
      </div>
      <div className="flex items-start gap-2">
        <span className="text-sm text-gray-700 dark:text-zinc-300">
          {description}
        </span>
      </div>
      {emailId && onViewEmail && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-2"
          onClick={() => onViewEmail(emailId)}
        >
          <EyeIcon className="h-3.5 w-3.5 mr-1.5" />
          View email
        </Button>
      )}
    </div>
  );
}

function EmailGroupPopoverContent({
  group,
  onViewEmail,
  onViewThread,
}: {
  group: { emails: ActivityItem[]; id: string };
  onViewEmail?: (emailId: string) => void;
  onViewThread?: (group: { emails: ActivityItem[] }) => void;
}) {
  const latestEmail = group.emails[group.emails.length - 1];
  const timeAgo = formatDistanceToNow(new Date(latestEmail.timestamp), {
    addSuffix: false,
  });
  const fullDateTime = formatFullDateTime(latestEmail.timestamp);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-700">
          <EnvelopeIcon className="h-3 w-3 text-gray-600 dark:text-white" />
        </div>
        <span className="text-xs font-medium text-gray-900 dark:text-zinc-100">
          {group.emails.length} emails
        </span>
        <span
          className="text-xs text-gray-500 dark:text-zinc-500"
          title={fullDateTime}
        >
          {timeAgo} ago
        </span>
      </div>
      {onViewThread && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => onViewThread(group)}
        >
          <EyeIcon className="h-3.5 w-3.5 mr-1.5" />
          View as thread
        </Button>
      )}
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {group.emails.map((email) => {
          const emailFrom = email.metadata?.from as string | undefined;
          const subject = email.metadata?.subject as string | undefined;
          const emailId = email.metadata?.emailId as string | undefined;
          return (
            <button
              key={email.id}
              type="button"
              className="flex items-center gap-2 w-full text-left text-xs text-gray-600 dark:text-zinc-400 pl-7 py-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
              onClick={() => emailId && onViewEmail?.(emailId)}
            >
              <div className="flex-1 min-w-0">
                <span className="font-medium">{emailFrom || 'Unknown'}</span>
                {subject && <span className="truncate"> · {subject}</span>}
              </div>
              {emailId && onViewEmail && (
                <EyeIcon className="h-3 w-3 shrink-0 opacity-50" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ActivityIcon({
  item,
  entityType,
  isPinned,
  onPinChange,
  onViewEmail,
  onViewThread,
}: {
  item: GroupedActivityItem;
  entityType: SystemEntity;
  isPinned: boolean;
  onPinChange: (pinned: boolean) => void;
  onViewEmail?: (emailId: string) => void;
  onViewThread?: (group: { emails: ActivityItem[] }) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isGroup = isEmailGroup(item);
  const type = isGroup ? 'email' : item.type;
  const metadata = isGroup ? undefined : item.metadata;
  const config = getActivityTypeConfig(type, metadata);
  const Icon = config.icon;
  const emailCount = isGroup ? item.emails.length : 0;

  const isOpen = isHovered || isPinned;

  const handlePinToggle = () => {
    onPinChange(!isPinned);
  };

  return (
    <Popover open={isOpen} onOpenChange={() => {}}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all',
            config.bgColor,
            'hover:ring-2 hover:ring-offset-1',
            config.ringColor,
            isPinned && 'ring-2 ring-offset-1',
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handlePinToggle}
        >
          <Icon className={cn('h-3.5 w-3.5', config.iconColor)} />
          {isGroup && emailCount > 1 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-gray-600 text-[10px] font-medium text-white dark:bg-zinc-500">
              {emailCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64"
        align="start"
        side="left"
        sideOffset={8}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isGroup ? (
          <EmailGroupPopoverContent
            group={item}
            onViewEmail={onViewEmail}
            onViewThread={onViewThread}
          />
        ) : (
          <ActivityItemPopoverContent
            item={item}
            entityType={entityType}
            onViewEmail={onViewEmail}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function ActivityItemRow({
  item,
  entityType,
  onViewEmail,
}: {
  item: ActivityItem;
  entityType: SystemEntity;
  onViewEmail?: (emailId: string) => void;
}) {
  const config = getActivityTypeConfig(item.type, item.metadata);
  const Icon = config.icon;
  const description = formatActivityDescription(item, entityType);
  const timeAgo = formatDistanceToNow(new Date(item.timestamp), {
    addSuffix: false,
  });
  const fullDateTime = formatFullDateTime(item.timestamp);

  const emailId =
    item.type === 'email' ? (item.metadata?.emailId as string) : null;
  const isClickable = !!emailId && !!onViewEmail;

  const content = (
    <>
      <div
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
          config.bgColor,
        )}
      >
        <Icon className={cn('h-3 w-3', config.iconColor)} />
      </div>

      <div className="flex flex-1 min-w-0 items-center gap-2">
        <span className="text-sm text-gray-700 dark:text-zinc-300 truncate">
          {description}
        </span>
        <span
          className="text-xs text-gray-500 dark:text-zinc-500 shrink-0"
          title={fullDateTime}
        >
          · {timeAgo} ago
        </span>
      </div>
    </>
  );

  if (isClickable) {
    return (
      <button
        type="button"
        className="flex items-start gap-3 py-2 w-full text-left rounded hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors -mx-1 px-1 cursor-pointer"
        onClick={() => onViewEmail(emailId)}
      >
        {content}
      </button>
    );
  }

  return <div className="flex items-start gap-3 py-2">{content}</div>;
}

function EmailGroupExpandedRow({
  group,
  entityType,
  onViewEmail,
  onViewThread,
}: {
  group: { emails: ActivityItem[]; id: string };
  entityType: SystemEntity;
  onViewEmail?: (emailId: string) => void;
  onViewThread?: (group: { emails: ActivityItem[] }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const emailCount = group.emails.length;
  const config = getActivityTypeConfig('email');
  const Icon = config.icon;
  const latestEmail = group.emails[group.emails.length - 1];
  const timeAgo = formatDistanceToNow(new Date(latestEmail.timestamp), {
    addSuffix: false,
  });
  const fullDateTime = formatFullDateTime(latestEmail.timestamp);

  return (
    <div className="py-2">
      <div className="flex w-full items-start gap-3">
        <div
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
            config.bgColor,
          )}
        >
          <Icon className={cn('h-3 w-3', config.iconColor)} />
        </div>

        <div className="flex flex-1 flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2">
            <Button
              variant="link"
              onClick={() => setIsOpen(!isOpen)}
              className="h-auto p-0 text-sm font-normal text-gray-700 dark:text-zinc-300"
            >
              {emailCount} emails · {isOpen ? 'hide' : 'show'}
            </Button>
            {onViewThread && (
              <Button
                variant="link"
                onClick={() => onViewThread(group)}
                className="h-auto p-0 text-xs font-normal flex items-center gap-1"
              >
                <EyeIcon className="h-3 w-3" />
                view thread
              </Button>
            )}
          </div>
          <span
            className="text-xs text-gray-500 dark:text-zinc-500"
            title={fullDateTime}
          >
            · {timeAgo} ago
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="mt-2 space-y-1 pl-9 border-l-2 border-gray-200 dark:border-zinc-700 ml-3">
          {group.emails.map((email) => (
            <ActivityItemRow
              key={email.id}
              item={email}
              entityType={entityType}
              onViewEmail={onViewEmail}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3 p-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-6 w-6 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex items-center justify-center py-8 text-sm text-gray-500 dark:text-zinc-500">
      No activity yet
    </div>
  );
}

const POLLING_INTERVAL_MS = 5000;

export function ActivityLogTab({ entityType, entityId }: ActivityLogTabProps) {
  const context = useActivityGraphContext();
  const { data, isLoading, error } = useActivityLog(entityType, entityId, {
    refetchInterval: POLLING_INTERVAL_MS,
  });
  const [pinnedItemId, setPinnedItemId] = useState<string | null>(null);

  const handleViewEmail = (emailId: string) => {
    if (context) {
      context.viewEmail(emailId);
    }
    setPinnedItemId(null);
  };

  const handleViewEmailGroup = (group: { emails: ActivityItem[] }) => {
    const emailIds = group.emails
      .map((email) => email.metadata?.emailId as string | undefined)
      .filter((id): id is string => !!id);
    if (emailIds.length > 0 && context) {
      context.viewEmailThread(emailIds);
    }
    setPinnedItemId(null);
  };

  const groupedItems = useMemo(() => {
    if (!data?.items?.length) return [];
    return groupConsecutiveEmails(data.items);
  }, [data?.items]);

  if (error) {
    return (
      <div className="flex items-center justify-center p-4 text-sm text-destructive">
        Failed to load activity
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!groupedItems.length) {
    return <EmptyState />;
  }

  return (
    <div className="p-3 space-y-1 divide-y divide-gray-100 dark:divide-zinc-800">
      {groupedItems.map((item) => {
        if (isEmailGroup(item)) {
          return (
            <EmailGroupExpandedRow
              key={item.id}
              group={item}
              entityType={entityType}
              onViewEmail={handleViewEmail}
              onViewThread={handleViewEmailGroup}
            />
          );
        }
        return (
          <ActivityItemRow
            key={item.id}
            item={item}
            entityType={entityType}
            onViewEmail={handleViewEmail}
          />
        );
      })}
    </div>
  );
}
